"use client";
import React, { useState } from "react";
import { FiCheckCircle } from "react-icons/fi";
import { FiXCircle } from "react-icons/fi";
import { MdHelp, MdSpaceDashboard } from "react-icons/md";
import { BsFillCollectionFill } from "react-icons/bs";
import { LuDatabase } from "react-icons/lu";
import {
  startImport,
  pollImportStatus,
  MetabaseConnectionData,
  loadPreviewData,
} from "~/lib/network/metabase-connection";

type DiscoverContentProps = {
  connectionData: MetabaseConnectionData;
  isSuccessful: boolean;
  handleNext: () => void;
  setIsSuccessful: (value: boolean) => void;
  previewData: any[];
  setPreviewData: (data: any[]) => void;
  setJobId: (jobId: string) => void;
  jobId: string | null;
};

interface ImportJob {
  status: string;
  progress: number;
  currentStep: string | null;
  error: string | null;
  isLoading: boolean;
  discoveredItems?: {
    databases: number;
    questions: number;
    dashboards: number;
    collections: number;
    total_items: number;
  };
}

export default function DiscoverContent({
  connectionData,
  isSuccessful,
  handleNext,
  setIsSuccessful,
  previewData,
  setPreviewData,
  setJobId,
  jobId,
}: DiscoverContentProps) {
  const [importJob, setImportJob] = useState<ImportJob>({
    status: "",
    progress: 0,
    currentStep: null,
    error: null,
    isLoading: false,
  });

  const handleStartImport = async (autoMigrate: boolean = false) => {
    setImportJob((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await startImport({
        metabaseUrl: connectionData.metabaseUrl,
        apiKey: connectionData.apiKey,
        autoMigrate: autoMigrate,
      });

      if (response?.job_id) {
        setImportJob({
          status: response?.status || "",
          progress: 0,
          currentStep: "Starting discovery...",
          error: null,
          isLoading: false,
        });

        // Pass jobId to parent
        setJobId(response.job_id);

        // Start polling for status updates
        pollStatus(response.job_id);
      }
    } catch (error: any) {
      setImportJob((prev) => ({
        ...prev,
        isLoading: false,
        error: error?.response?.data?.detail || "Failed to start import",
      }));
    }
  };

  const pollStatus = async (jobId: string) => {
    try {
      const data = await pollImportStatus(jobId);

      setImportJob((prev) => ({
        ...prev,
        status: data?.status || "",
        progress: data?.progress_percentage || 0,
        currentStep: data?.current_step || null,
        error: data?.error_message || null,
        discoveredItems: data?.discovered_items || undefined,
      }));

      // Continue polling if still in progress
      if (data?.status === "in_progress" || data?.status === "pending") {
        setTimeout(() => pollStatus(jobId), 2000);
      } else if (data?.status === "preview_ready") {
        // Load preview data when ready
        handlePreviewData(jobId);
        setIsSuccessful(true); // Set success when discovery completes
      }
    } catch (error) {
      setImportJob((prev) => ({
        ...prev,
        error: `Status polling error: ${error}`,
      }));
    }
  };

  const handlePreviewData = async (jobId: string) => {
    const response = await loadPreviewData(jobId);
    setPreviewData(response || []);
  };

  return (
    <div className="w-full">
      {/* Discovery Section */}
      <h2 className="text-xl font-bold mb-4">2. Discover Content</h2>
      <p className="mb-4">
        Analyze your Metabase instance to see what can be migrated
      </p>

      {jobId === null && (
        <div className="flex flex-wrap gap-4 my-10 justify-center">
          <button
            onClick={() => handleStartImport(false)}
            disabled={importJob?.isLoading || jobId !== null}
            className="btn bg-purple-600 text-white hover:bg-purple-500"
          >
            {importJob?.isLoading
              ? "Starting Discovery..."
              : "Discover Content (Preview First)"}
          </button>

          <button
            onClick={() => handleStartImport(true)}
            disabled={importJob?.isLoading || jobId !== null}
            className="btn btn-secondary"
          >
            Discover & Auto-Migrate
          </button>
        </div>
      )}

      {importJob?.error && (
        <div className="alert alert-error mt-4">
          <FiXCircle className="h-6 w-6" />
          <strong>Import Error:</strong> {importJob.error}
        </div>
      )}

      {/* Progress Section */}
      {jobId &&
        (importJob?.status === "in_progress" ||
          importJob?.status === "pending" ||
          importJob?.isLoading) && (
          <div className="flex flex-col items-center my-6">
            <div
              className="radial-progress text-primary"
              style={
                { "--value": importJob?.progress || 0 } as React.CSSProperties
              }
              aria-valuenow={importJob?.progress || 0}
              role="progressbar"
            >
              {importJob?.progress || 0}%
            </div>
            {importJob?.currentStep && (
              <p className="text-sm text-base-content/70 mt-2 text-center">
                {importJob.currentStep}
              </p>
            )}
          </div>
        )}
      {jobId && importJob?.discoveredItems && (
        <div className="mt-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <FiCheckCircle className="text-success" />
            Results
          </h3>
          <p className="mb-6">
            Found{" "}
            <strong>{importJob?.discoveredItems?.total_items || 0}</strong>{" "}
            items in your Metabase:
          </p>
          <div className="stats">
            <div className="stat">
              <div className="stat-figure text-primary opacity-90">
                <LuDatabase size={12} className="w-6 h-6" />
              </div>
              <div className="stat-title">Database Connections</div>
              <div className="stat-value">
                {importJob?.discoveredItems?.databases || 0}
              </div>
              <div className="stat-desc text-xs">Ready to migrate</div>
            </div>

            <div className="stat">
              <div className="stat-figure text-info opacity-90">
                <MdHelp size={12} className="w-6 h-6" />
              </div>
              <div className="stat-title">Saved Questions</div>
              <div className="stat-value">
                {importJob?.discoveredItems?.questions || 0}
              </div>
              <div className="stat-desc text-xs">SQL queries & charts</div>
            </div>

            <div className="stat">
              <div className="stat-figure text-lime-400 opacity-90">
                <MdSpaceDashboard size={12} className="w-6 h-6" />
              </div>
              <div className="stat-title">Dashboards</div>
              <div className="stat-value">
                {importJob?.discoveredItems?.dashboards || 0}
              </div>
              <div className="stat-desc text-xs">Interactive reports</div>
            </div>

            <div className="stat">
              <div className="stat-figure text-purple-400">
                <BsFillCollectionFill className="w-6 h-6" />
              </div>
              <div className="stat-title">Collections</div>
              <div className="stat-value">
                {importJob?.discoveredItems?.collections || 0}
              </div>
              <div className="stat-desc text-xs">Organized content</div>
            </div>
          </div>

          <button
            type="button"
            className="btn bg-purple-600 text-white hover:bg-purple-500 mx-auto w-full mt-6 max-w-md block"
            disabled={!isSuccessful}
            onClick={handleNext}
          >
            Review Migration
          </button>
        </div>
      )}
    </div>
  );
}
